import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { userApi, UserProfile } from '../services/api-client';
import { identifyUser } from '../services/analytics';

export interface AuthUser {
  username: string;
  email?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  profile: UserProfile | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  checkAuthState: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();

      // Get the actual user ID (sub) from the session
      const session = await fetchAuthSession();
      const userId = session.tokens?.idToken?.payload?.sub as string;

      const resolvedUserId = userId || currentUser.username;
      setUser({
        username: resolvedUserId, // Use sub (UUID) instead of username
        email: currentUser.signInDetails?.loginId,
      });
      identifyUser(resolvedUserId);

      // Check if user is an admin by parsing JWT token
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;

        if (idToken && idToken.payload) {
          const groups = idToken.payload['cognito:groups'];
          const userIsAdmin = Array.isArray(groups)
            ? groups.includes('admins')
            : groups === 'admins';
          setIsAdmin(userIsAdmin);
          console.log('User admin status:', userIsAdmin);
        } else {
          setIsAdmin(false);
        }
      } catch (sessionError) {
        console.error('Failed to check admin status:', sessionError);
        setIsAdmin(false);
      }

      // Try to load user profile, or auto-register if OAuth user
      try {
        const userProfile = await userApi.getProfile();
        setProfile(userProfile);
      } catch (profileError) {
        // Profile doesn't exist yet - auto-register (for OAuth and first-time users)
        console.log('Profile not found, auto-registering user...');
        try {
          await userApi.register();
          const userProfile = await userApi.getProfile();
          setProfile(userProfile);
          console.log('User auto-registered successfully');
        } catch (registerError: any) {
          const errorMessage = registerError?.message || '';
          if (!errorMessage.includes('already registered')) {
            console.error('Failed to auto-register user:', registerError);
          }
          setProfile(null);
        }
      }
    } catch (error) {
      setUser(null);
      setProfile(null);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuthState();
  }, []);

  const value: AuthContextType = {
    user,
    profile,
    isAuthenticated: !!user,
    isAdmin,
    isLoading,
    checkAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
